const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'https://api.ledscroller.com';

const postJson = async (path, body) => {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error((data && data.message) || 'Request failed');
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
};

const authHeaders = (token) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export const createCheckoutSession = (guestAccessCode) =>
  postJson('/checkout/create-session', { guestAccessCode });

export const unlockToken = ({ orderId, guestAccessCode }) =>
  postJson('/verify/unlock-token', { orderId, guestAccessCode });

export const recoverOrder = ({ orderId, guestAccessCode }) =>
  postJson('/orders/recover', { orderId, guestAccessCode });

// Local/dev only: BE returns 404 unless ENABLE_DEV_PAYMENT_SIM=true
export const simulatePaidIfAvailable = async (orderId) => {
  if (!orderId) {
    return { skipped: true, reason: 'no_order' };
  }
  try {
    const response = await fetch(`${API_BASE}/demo/simulate-paid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    });
    if (response.status === 404) {
      return { skipped: true, reason: 'not_enabled' };
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        skipped: true,
        reason: 'error',
        message: (data && data.message) || 'simulate failed',
      };
    }
    return { skipped: false, ...data };
  } catch (e) {
    return { skipped: true, reason: 'network' };
  }
};

export const signup = (payload) => postJson('/auth/signup', payload);

export const verifyOtp = (payload) => postJson('/auth/verify-otp', payload);

export const resendOtp = (email) => postJson('/auth/resend-otp', { email });

export const forgotPassword = (email) =>
  postJson('/auth/forgot-password', { email });

export const resendForgotOtp = (email) =>
  postJson('/auth/forgot-password/resend-otp', { email });

export const verifyForgotOtp = (payload) =>
  postJson('/auth/forgot-password/verify-otp', payload);

export const resetPassword = (payload) =>
  postJson('/auth/forgot-password/reset', payload);

export const login = (payload) => postJson('/auth/login', payload);

export const loginAccessCode = (guestAccessCode) =>
  postJson('/auth/login-access-code', { guestAccessCode });

export const fetchProfile = async (token) => {
  const response = await fetch(`${API_BASE}/profile/me`, {
    headers: authHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || 'Failed to load profile');
  }
  return data;
};

export const fetchOrders = async (token) => {
  const response = await fetch(`${API_BASE}/profile/orders`, {
    headers: authHeaders(token),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || 'Failed to load orders');
  }
  return data;
};

export const sendReceipt = async (token, orderId) => {
  const response = await fetch(
    `${API_BASE}/profile/orders/${encodeURIComponent(orderId)}/send-receipt`,
    { method: 'POST', headers: authHeaders(token) }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || 'Failed to send receipt');
  }
  return data;
};

export const updateProfile = async (token, payload) => {
  const response = await fetch(`${API_BASE}/profile/me`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || 'Failed to update profile');
  }
  return data;
};

export const claimAccessCode = async (token, guestAccessCode) => {
  const response = await fetch(`${API_BASE}/profile/claim`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ guestAccessCode }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || 'Failed to link access code');
  }
  return data;
};

export const changePassword = async (token, payload) => {
  const response = await fetch(`${API_BASE}/profile/change-password`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(payload || {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || 'Failed to change password');
  }
  return data;
};

export const logout = () => postJson('/auth/logout', {});

export default {
  createCheckoutSession,
  unlockToken,
  recoverOrder,
};
